/**
 * Admin Project Viewer — full-screen viewer for admins
 * Mirrors PortalProjectViewer but loads via api.project.get() and links back to admin.
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../services/api';
import { AppProvider, useApp } from '../../store/AppContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { ViewerRail } from '../../components/layout/ViewerRail';
import { TABS, getEnabledTabs } from '../../../shared/types/project';
import type { Project, TabId, DownloadAsset } from '../../../shared/types/project';
import { formatProjectName } from '../../../shared/formatProjectName';
import coreskanLogo from '../../assets/coreskan-logo.png';
import '../../components/layout/TabBar.css';
import '../portal/portal-responsive.css';
import '../../components/layout/viewer-chrome.css';
import { useAuth } from '../../contexts/AuthContext';

export function AdminProjectViewer() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    api.project.get(id)
      .then(setProject)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div style={{ color: '#94a3b8', padding: 32 }}>Loading project...</div>;
  if (error) return <div style={{ color: '#ef4444', padding: 32 }}>{error}</div>;
  if (!project) return <div style={{ color: '#ef4444', padding: 32 }}>Project not found</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', minHeight: 0, position: 'relative' }}>
      <AppProvider>
        <ProjectLoader project={project} />
      </AppProvider>
    </div>
  );
}

// ─── Tab icons (same SVGs used by PortalProjectViewer) ───────────────────────

const TabIcons: Record<string, React.ReactNode> = {
  tour: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  cube: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <path d="M3.27 6.96L12 12.01l8.73-5.05" />
      <path d="M12 22.08V12" />
    </svg>
  ),
  scatter: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="7" cy="7" r="2" /><circle cx="17" cy="7" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="7" cy="17" r="2" /><circle cx="17" cy="17" r="2" />
      <circle cx="5" cy="12" r="1.5" /><circle cx="19" cy="12" r="1.5" />
    </svg>
  ),
  map: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 6v16l7-4 8 4 7-4V2l-7 4-8-4-7 4z" />
      <path d="M8 2v16" /><path d="M16 6v16" />
    </svg>
  ),
  temperature: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" />
    </svg>
  ),
};

// ─── Admin Navigation Bar ────────────────────────────────────────────────────

function AdminNav({ project }: { project: Project }) {
  const { state, setActiveTab, togglePdfPanel, toggleDualView, dispatch } = useApp();
  const { user } = useAuth();
  const initials = (user?.name || user?.email || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const [menuOpen, setMenuOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const fabRef = useRef<HTMLDivElement>(null);
  const downloadRef = useRef<HTMLDivElement>(null);

  const enabledTabIds = getEnabledTabs(state.currentProject);
  const visibleTabs = TABS.filter(tab => enabledTabIds.includes(tab.id));
  const enabledDownloads = (project.downloads || []).filter(d => d.enabled);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen && !downloadOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuOpen && fabRef.current && !fabRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
      if (downloadOpen && downloadRef.current && !downloadRef.current.contains(e.target as Node)) {
        setDownloadOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen, downloadOpen]);

  const handleTabClick = (tabId: TabId) => {
    if (state.dualViewMode) dispatch({ type: 'TOGGLE_DUAL_VIEW' });
    setActiveTab(tabId);
    setMenuOpen(false);
  };

  return (
    <>
      {/* Desktop / tablet nav bar */}
      <nav className="unified-nav">
        {/* Left: brand + back to admin */}
        <div className="unified-nav__left">
          <Link to={`/admin/projects/${project.id}`} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', opacity: 0.85 }} title="Back to Admin">
            <img src={coreskanLogo} alt="CoreSkan" className="unified-nav__logo" />
          </Link>
          <span className="unified-nav__separator" />
          <span className="unified-nav__project-name">{formatProjectName(project)}</span>
        </div>

        {/* Right: actions + admin links */}
        <div className="unified-nav__right">
          <button
            className={`tabbar__action ${state.dualViewMode ? 'tabbar__action--active' : ''}`}
            onClick={toggleDualView}
            title="Split View"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="18" rx="1" />
              <rect x="14" y="3" width="7" height="18" rx="1" />
            </svg>
            <span>Split</span>
          </button>
          <button
            className={`tabbar__action ${state.pdfPanelOpen ? 'tabbar__action--active' : ''}`}
            onClick={togglePdfPanel}
            title="Toggle PDF Panel (Ctrl+P)"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <path d="M14 2v6h6" /><path d="M16 13H8" /><path d="M16 17H8" /><path d="M10 9H8" />
            </svg>
            <span>PDF</span>
          </button>

          {enabledDownloads.length > 0 && (
            <div style={{ position: 'relative' }} ref={downloadRef}>
              <button
                className={`tabbar__action ${downloadOpen ? 'tabbar__action--active' : ''}`}
                onClick={() => setDownloadOpen(v => !v)}
                title="Downloads"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <span>Downloads</span>
              </button>
              {downloadOpen && <DownloadDropdown downloads={enabledDownloads} onClose={() => setDownloadOpen(false)} />}
            </div>
          )}

          <span className="unified-nav__separator" />

          <Link to="/admin/projects" className="unified-nav__iconlink" title="Back to Admin">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="8" height="8" rx="2" /><rect x="13" y="3" width="8" height="8" rx="2" />
              <rect x="3" y="13" width="8" height="8" rx="2" /><rect x="13" y="13" width="8" height="8" rx="2" />
            </svg>
          </Link>
          {user?.profilePhotoPath ? (
            <div className="unified-nav__avatar unified-nav__avatar--photo" style={{ backgroundImage: `url(${user.profilePhotoPath})` }} title={user?.name || user?.email} />
          ) : (
            <div className="unified-nav__avatar unified-nav__avatar--initials" title={user?.name || user?.email}>{initials}</div>
          )}
        </div>
      </nav>

      {/* Floating menu overlay (visible only on mobile) */}
      <div className="unified-fab" ref={fabRef}>
        <button
          className="unified-fab__button"
          onClick={() => setMenuOpen(v => !v)}
          aria-label="Menu"
        >
          {menuOpen ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18" /><path d="M6 6l12 12" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12h18" /><path d="M3 6h18" /><path d="M3 18h18" />
            </svg>
          )}
        </button>

        {menuOpen && (
          <div className="unified-fab__dropdown">
            <div className="unified-fab__section-label">Deliverables</div>
            {visibleTabs.map(tab => {
              const active = state.activeTab === tab.id && !state.dualViewMode;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={`unified-fab__item ${active ? 'unified-fab__item--active' : ''}`}
                >
                  <span className="unified-fab__icon">{TabIcons[tab.icon]}</span>
                  {tab.label}
                </button>
              );
            })}

            <div className="unified-fab__divider" />
            <div className="unified-fab__section-label">View</div>
            <button
              onClick={() => { toggleDualView(); setMenuOpen(false); }}
              className={`unified-fab__item ${state.dualViewMode ? 'unified-fab__item--active' : ''}`}
            >
              <span className="unified-fab__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="18" rx="1" />
                  <rect x="14" y="3" width="7" height="18" rx="1" />
                </svg>
              </span>
              Split View
            </button>
            <button
              onClick={() => { togglePdfPanel(); setMenuOpen(false); }}
              className={`unified-fab__item ${state.pdfPanelOpen ? 'unified-fab__item--active' : ''}`}
            >
              <span className="unified-fab__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6" /><path d="M16 13H8" /><path d="M16 17H8" /><path d="M10 9H8" />
                </svg>
              </span>
              PDF Panel
            </button>

            {enabledDownloads.length > 0 && (
              <>
                <div className="unified-fab__divider" />
                <div className="unified-fab__section-label">Downloads</div>
                {enabledDownloads.map(dl => (
                  <a
                    key={dl.id}
                    href={dl.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="unified-fab__item"
                    onClick={() => setMenuOpen(false)}
                    style={{ textDecoration: 'none' }}
                  >
                    <span className="unified-fab__icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    </span>
                    {dl.name}
                  </a>
                ))}
              </>
            )}

            <div className="unified-fab__divider" />
            <Link
              to="/admin/projects"
              className="unified-fab__item"
              onClick={() => setMenuOpen(false)}
              style={{ textDecoration: 'none' }}
            >
              Back to Admin
            </Link>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Downloads Dropdown ──────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1073741824).toFixed(1)} GB`;
}

const FILE_TYPE_COLORS: Record<string, string> = {
  // Point Clouds — green
  las: '#00cc88', laz: '#00cc88', e57: '#00cc88', ply: '#00cc88', pts: '#00cc88', xyz: '#00cc88', rcs: '#00cc88', rcp: '#00cc88',
  // CAD / BIM — amber
  dwg: '#f59e0b', dxf: '#f59e0b', ifc: '#8b5cf6', rvt: '#8b5cf6', nwd: '#8b5cf6', nwc: '#8b5cf6',
  // Documents — red/blue
  pdf: '#ef4444', docx: '#3b82f6', doc: '#3b82f6', pptx: '#f97316', xlsx: '#22c55e', csv: '#22c55e',
  // 3D Models — purple
  obj: '#a855f7', fbx: '#a855f7', glb: '#a855f7', gltf: '#a855f7', stl: '#a855f7', dae: '#a855f7',
  // Images — cyan
  jpg: '#06b6d4', jpeg: '#06b6d4', png: '#06b6d4', tif: '#06b6d4', tiff: '#06b6d4', webp: '#06b6d4',
  // GIS — violet
  shp: '#a855f7', kmz: '#a855f7', kml: '#a855f7', geojson: '#a855f7',
  // Archives — slate
  zip: '#64748b', rar: '#64748b', '7z': '#64748b', tar: '#64748b', gz: '#64748b',
};

const FILE_TYPE_CATEGORY: Record<string, string> = {
  las: 'Point Clouds', laz: 'Point Clouds', e57: 'Point Clouds', ply: 'Point Clouds', pts: 'Point Clouds', xyz: 'Point Clouds', rcs: 'Point Clouds', rcp: 'Point Clouds',
  dwg: 'CAD', dxf: 'CAD',
  ifc: 'BIM', rvt: 'BIM', nwd: 'BIM', nwc: 'BIM',
  pdf: 'Documents', docx: 'Documents', doc: 'Documents', pptx: 'Documents', xlsx: 'Documents', csv: 'Documents',
  obj: '3D Models', fbx: '3D Models', glb: '3D Models', gltf: '3D Models', stl: '3D Models', dae: '3D Models',
  jpg: 'Images', jpeg: 'Images', png: 'Images', tif: 'Images', tiff: 'Images', webp: 'Images',
  shp: 'GIS', kmz: 'GIS', kml: 'GIS', geojson: 'GIS',
  zip: 'Archives', rar: 'Archives', '7z': 'Archives', tar: 'Archives', gz: 'Archives',
};

function groupDownloads(downloads: DownloadAsset[]): { category: string; items: DownloadAsset[] }[] {
  const groups = new Map<string, DownloadAsset[]>();
  for (const dl of downloads) {
    const cat = FILE_TYPE_CATEGORY[dl.fileType || ''] || 'Other';
    if (!groups.has(cat)) groups.set(cat, []);
    groups.get(cat)!.push(dl);
  }
  return Array.from(groups.entries()).map(([category, items]) => ({ category, items }));
}

function DownloadDropdown({ downloads, onClose }: { downloads: DownloadAsset[]; onClose: () => void }) {
  const groups = groupDownloads(downloads);
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div style={{
      position: 'absolute', top: '100%', right: 0, marginTop: 8,
      background: 'rgba(26, 26, 26, 0.95)', backdropFilter: 'blur(12px)',
      border: '1px solid #333', borderRadius: 12, padding: '6px 0',
      minWidth: 260, maxWidth: 360, maxHeight: 400, overflowY: 'auto',
      zIndex: 1001, boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
    }}>
      {groups.map(({ category, items }) => {
        const isOpen = expanded === category;
        const catColor = FILE_TYPE_COLORS[items[0]?.fileType || ''] || '#94a3b8';
        return (
          <div key={category}>
            <button
              onClick={() => setExpanded(isOpen ? null : category)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                padding: '10px 16px', border: 'none', background: 'transparent',
                color: '#e2e8f0', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                textAlign: 'left', transition: 'background 0.1s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: catColor, flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{category}</span>
              <span style={{ fontSize: 11, color: '#475569', marginRight: 4 }}>{items.length}</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2"
                style={{ transition: 'transform 0.15s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0)' }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {isOpen && items.map(dl => {
              const typeColor = FILE_TYPE_COLORS[dl.fileType || ''] || '#94a3b8';
              const typeLabel = (dl.fileType || 'file').toUpperCase().slice(0, 4);
              return (
                <a key={dl.id} href={dl.url} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px 8px 34px', textDecoration: 'none', color: '#e2e8f0', fontSize: 13, transition: 'background 0.1s' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  onClick={onClose}
                >
                  <div style={{ width: 24, height: 24, borderRadius: 4, background: `${typeColor}15`, border: `1px solid ${typeColor}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 7, fontWeight: 700, color: typeColor, flexShrink: 0 }}>
                    {typeLabel}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 400, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{dl.name}</div>
                  </div>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#4F7CFF" strokeWidth="2" style={{ flexShrink: 0 }}>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </a>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ─── Inner component that loads project into AppContext ───────────────────────

function ProjectLoader({ project }: { project: Project }) {
  const { dispatch } = useApp();

  useEffect(() => {
    dispatch({ type: 'SET_CURRENT_PROJECT', payload: project });
    dispatch({ type: 'SET_VIEW_MODE', payload: 'project' });
  }, [project, dispatch]);

  return (
    <>
      <AdminNav project={project} />
      <div className="viewer-body">
        <ViewerRail links={[{ key: 'data', label: 'Data Management', to: `/admin/projects/${project.id}`, icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg> }]} />
        <MainLayout />
      </div>
    </>
  );
}
